import React, { useEffect, useMemo, useState, useRef } from "react";
import { useCompanion } from "@/companion/CompanionContext";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import InventoryShell from "./InventoryShell";
import {
  Flame, Droplet, Sparkles, Gem, Diamond, Gift, Boxes, FlaskConical,
  Play, Pause, Search, Check, ChevronRight, StickyNote, Minus, Plus,
} from "lucide-react";

/**
 * OHQ Audit landing + workspace — Phase I2.
 *
 * One React file to keep the workflow legible; three visible states:
 *   1) No open session → landing with Start Audit CTA + explanation
 *   2) Open session, no category focus → Category Ladder
 *   3) Category focus (sessionId + category in URL) → Product Count Cards
 *
 * Autosave: PATCH /inventory/audit/lines/:id on Enter or blur, debounced by
 * per-line dirty timer. Progress advances live from the server response.
 */

const CATEGORY_ICONS = {
  candles: Flame,
  oils: Droplet,
  lotions: Sparkles,
  jewelry: Gem,
  crystals: Diamond,
  gift_packaging: Gift,
  supplies: Boxes,
  back_bar: FlaskConical,
};

const CATEGORY_LABELS = {
  candles: "Candles",
  oils: "Oils",
  lotions: "Lotions",
  jewelry: "Jewelry",
  crystals: "Crystals",
  gift_packaging: "Gift Packaging",
  supplies: "Supplies",
  back_bar: "Back Bar",
};


export default function RetailAudit() {
  const companion = useCompanion();
  const { sessionId, category } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [lines, setLines] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [savedPill, setSavedPill] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const savedPillTimer = useRef(null);

  // ---------- Load ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        if (sessionId) {
          const r = await api.get(`/inventory/audit/sessions/${sessionId}`);
          if (!alive) return;
          // I3 hand-off: finalized or closed sessions live on the variance review surface.
          const st = r.data.session?.status;
          if (st === "variance_review" || st === "closed") {
            navigate(`/operations/inventory/audit/${r.data.session.id}/variances`, { replace: true });
            return;
          }
          setSession(r.data.session);
          setLines(r.data.lines);
          setProgress(r.data.progress);
        } else {
          const r = await api.get("/inventory/audit/sessions/current");
          if (!alive) return;
          if (r.data.session) {
            // Auto-hop into the open session's URL so refresh + share works
            navigate(`/operations/inventory/audit/${r.data.session.id}`, { replace: true });
          } else {
            setSession(null);
            setLines([]);
          }
        }
      } catch (e) {
        console.error("audit load", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [sessionId, navigate]);

  // ---------- Finalize (I3 hand-off) ----------
  const finalizeAudit = async () => {
    if (!session) return;
    try {
      await api.post(`/inventory/audit/sessions/${session.id}/finalize`, {});
      navigate(`/operations/inventory/audit/${session.id}/variances`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not finalize audit.");
    }
  };

  // ---------- Start / pause / resume ----------
  const startAudit = async () => {
    setStarting(true);
    try {
      const r = await api.post("/inventory/audit/sessions", { kind: "retail" });
      navigate(`/operations/inventory/audit/${r.data.session.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start audit";
      alert(msg);
    } finally {
      setStarting(false);
    }
  };

  const pauseOrResume = async () => {
    if (!session) return;
    const action = session.status === "paused" ? "resume" : "pause";
    try {
      await api.post(`/inventory/audit/sessions/${session.id}/${action}`);
      setSession({ ...session, status: action === "pause" ? "paused" : "in_progress" });
    } catch (e) { console.error("pause/resume", e); }
  };

  // ---------- Autosave a single line ----------
  const saveLine = async (lineId, patch) => {
    try {
      const r = await api.patch(`/inventory/audit/lines/${lineId}`, patch);
      // Replace the line in local state; server owns the truth.
      setLines((prev) => prev.map((ln) => (ln.id === lineId ? r.data.line : ln)));
      setProgress(r.data.progress);
      flashSavedPill();
    } catch (e) {
      console.error("saveLine", e);
    }
  };
  const flashSavedPill = () => {
    setSavedPill(true);
    clearTimeout(savedPillTimer.current);
    savedPillTimer.current = setTimeout(() => setSavedPill(false), 1500);
  };

  // ---------- Derived groupings ----------
  const linesByCategory = useMemo(() => {
    const m = {};
    for (const ln of lines) {
      (m[ln.category] ||= []).push(ln);
    }
    return m;
  }, [lines]);

  const categoryList = useMemo(() => {
    const cats = Object.keys(linesByCategory);
    // Sort by canonical order defined on the server-side taxonomy.
    const order = ["candles","oils","lotions","jewelry","crystals","gift_packaging","supplies","back_bar"];
    cats.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return cats;
  }, [linesByCategory]);

  const visibleLines = useMemo(() => {
    let list = category ? (linesByCategory[category] || []) : lines;
    if (searchQ.trim()) {
      const q = searchQ.trim().toLowerCase();
      list = list.filter(
        (ln) =>
          ln.product_display_name.toLowerCase().includes(q) ||
          ln.product_sku.toLowerCase().includes(q)
      );
    }
    return list;
  }, [category, linesByCategory, lines, searchQ]);

  // ============================================================
  // Render 1: no session — landing
  // ============================================================
  if (loading) {
    return <InventoryShell title="OHQ Audit"><div className="text-sm text-stone-400">Loading…</div></InventoryShell>;
  }

  if (!session) {
    return (
      <InventoryShell title="OHQ Audit">
        <div
          className="max-w-xl rounded-2xl border border-stone-100 bg-white p-8"
          data-testid="inv-audit-landing-empty"
        >
          <div className="text-[10px] font-medium uppercase tracking-wider text-stone-400 mb-1">
            No audit in progress
          </div>
          <h2 className="font-serif-display text-2xl text-stone-900 mb-3">
            Ready when you are.
          </h2>
          <p className="text-sm text-stone-500 mb-6 leading-relaxed">
            OHQ Audit walks you through every SKU in your boutique — one category at a time.
            Your counts save as you go. You can pause and pick up from any device. When every
            category is at 100%, {companion.chatAssistantName} will help you review any discrepancies before closing.
          </p>
          <button
            onClick={startAudit}
            disabled={starting}
            data-testid="inv-audit-start"
            className="rounded-full bg-stone-900 text-white text-sm px-6 py-3 hover:bg-stone-700 transition-colors disabled:opacity-60"
          >
            {starting ? "Starting…" : "Start audit"}
          </button>
        </div>
      </InventoryShell>
    );
  }

  // ============================================================
  // Render 2/3: active session
  // ============================================================
  const totalPct = progress?.pct_complete ?? 0;
  const isPaused = session.status === "paused";

  return (
    <InventoryShell
      title={category ? CATEGORY_LABELS[category] || category : "OHQ Audit"}
      right={
        <div className="flex items-center gap-3">
          {savedPill && (
            <span
              data-testid="inv-audit-saved-pill"
              className="text-[10px] uppercase tracking-wider text-stone-400 flex items-center gap-1"
            >
              <Check size={11} /> Saved
            </span>
          )}
          <button
            onClick={pauseOrResume}
            data-testid="inv-audit-pause"
            className="flex items-center gap-1 rounded-full border border-stone-200 text-sm px-3 py-1.5 text-stone-700 hover:bg-stone-50"
          >
            {isPaused ? <><Play size={12} /> Resume</> : <><Pause size={12} /> Pause</>}
          </button>
        </div>
      }
    >
      {/* Global progress + search bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6" data-testid="inv-audit-header">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-stone-500">
              {progress?.lines_counted ?? 0} of {progress?.lines_total ?? 0} counted
            </span>
            <span className="text-xs text-stone-300">·</span>
            <span className="text-xs text-stone-500">
              {progress?.categories_complete ?? 0} / {progress?.categories_total ?? 0} categories done
            </span>
          </div>
          <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
            <div
              data-testid="inv-audit-progress-bar"
              className="h-full bg-stone-900 transition-all"
              style={{ width: `${totalPct}%` }}
            />
          </div>
        </div>
        <div className="sm:w-64 relative">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
          <input
            data-testid="inv-audit-search"
            type="text"
            placeholder="Search products…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="w-full rounded-full border border-stone-200 pl-8 pr-3 py-1.5 text-sm outline-none focus:border-stone-400"
          />
        </div>
      </div>

      {/* Paused banner */}
      {isPaused && !searchQ && !category && (
        <div
          className="mb-6 rounded-2xl border border-amber-100 bg-amber-50/50 p-4 flex items-center justify-between"
          data-testid="inv-audit-resume-strip"
        >
          <div className="text-sm text-stone-700">
            You paused this audit. Pick up right where you left off.
          </div>
          <button
            onClick={pauseOrResume}
            className="rounded-full bg-stone-900 text-white text-xs px-3 py-1.5 hover:bg-stone-700"
          >
            Resume
          </button>
        </div>
      )}

      {/* Ready to close — appears only at 100% counted, top of the ladder view */}
      {totalPct >= 100 && !category && !searchQ.trim() && (
        <div
          className="mb-6 rounded-2xl bg-emerald-50 border border-emerald-100 p-5 flex items-center justify-between gap-4"
          data-testid="inv-audit-ready-to-close"
        >
          <div>
            <div className="text-[10px] label-eyebrow text-emerald-800 mb-1">All categories counted</div>
            <div className="text-sm text-stone-800">
              You've counted every SKU. {companion.chatAssistantName} will help you review any discrepancies before you close the audit.
            </div>
          </div>
          <button
            onClick={finalizeAudit}
            data-testid="inv-audit-finish"
            className="text-sm px-5 py-2.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 whitespace-nowrap"
          >
            Finish audit
          </button>
        </div>
      )}

      {/* When searching or in a category — show product cards */}
      {(category || searchQ.trim()) ? (
        <ProductGrid
          lines={visibleLines}
          onSave={saveLine}
          disabled={isPaused}
          category={category}
          onBack={() => { setSearchQ(""); navigate(`/operations/inventory/audit/${session.id}`); }}
        />
      ) : (
        <CategoryLadder
          sessionId={session.id}
          categoryList={categoryList}
          linesByCategory={linesByCategory}
        />
      )}
    </InventoryShell>
  );
}


// ============================================================
// Category Ladder
// ============================================================
function CategoryLadder({ sessionId, categoryList, linesByCategory }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-2" data-testid="inv-audit-category-ladder">
      {categoryList.map((cat) => {
        const arr = linesByCategory[cat] || [];
        const counted = arr.filter((ln) => ln.counted_qty != null).length;
        const total = arr.length;
        const pct = total ? Math.round((counted / total) * 100) : 0;
        const Icon = CATEGORY_ICONS[cat] || Boxes;
        return (
          <button
            key={cat}
            onClick={() => navigate(`/operations/inventory/audit/${sessionId}/${cat}`)}
            data-testid={`inv-audit-cat-${cat}`}
            className="w-full flex items-center gap-4 rounded-2xl border border-stone-100 bg-white p-5 hover:bg-stone-50 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
              <Icon size={16} className="text-stone-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-stone-900">{CATEGORY_LABELS[cat] || cat}</div>
              <div className="text-xs text-stone-500 mt-0.5">
                {counted} of {total} counted
              </div>
              <div className="mt-2 h-1 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-stone-900 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <ChevronRight size={16} className="text-stone-300 shrink-0" />
          </button>
        );
      })}
    </div>
  );
}


// ============================================================
// Product Grid
// ============================================================
function ProductGrid({ lines, onSave, disabled, category, onBack }) {
  return (
    <div>
      <div className="mb-4">
        <button
          onClick={onBack}
          data-testid="inv-audit-back-to-ladder"
          className="text-xs text-stone-400 hover:text-stone-800"
        >
          ← All categories
        </button>
      </div>
      {lines.length === 0 ? (
        <div className="text-sm text-stone-400">No products match your search.</div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="inv-audit-product-grid"
        >
          {lines.map((ln) => (
            <ProductCountCard key={ln.id} line={ln} onSave={onSave} disabled={disabled} />
          ))}
        </div>
      )}
    </div>
  );
}


// ============================================================
// Product Count Card
// ============================================================
function ProductCountCard({ line, onSave, disabled }) {
  const [val, setVal] = useState(line.counted_qty != null ? String(line.counted_qty) : "");
  const [dirty, setDirty] = useState(false);
  const [noteOpen, setNoteOpen] = useState(!!line.notes);
  const [note, setNote] = useState(line.notes || "");

  useEffect(() => {
    setVal(line.counted_qty != null ? String(line.counted_qty) : "");
    setNote(line.notes || "");
  }, [line.counted_qty, line.notes]);

  const commit = () => {
    if (!dirty) return;
    setDirty(false);
    const n = val.trim() === "" ? null : Math.max(0, parseInt(val, 10) || 0);
    onSave(line.id, { counted_qty: n, notes: note });
  };

  const bump = (delta) => {
    if (disabled) return;
    const current = parseInt(val || "0", 10) || 0;
    const next = Math.max(0, current + delta);
    setVal(String(next));
    setDirty(false);
    onSave(line.id, { counted_qty: next, notes: note || null });
  };

  const sameAsExpected = () => {
    if (disabled) return;
    setVal(String(line.expected_qty));
    setDirty(false);
    onSave(line.id, { counted_qty: line.expected_qty, notes: note || null });
  };

  const variance = line.counted_qty != null ? line.counted_qty - line.expected_qty : null;
  const counted = line.counted_qty != null;
  const hasVariance = counted && variance !== 0;

  // Monogram fallback for product tile (no product images seeded yet)
  const monogram = line.product_display_name
    .split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  return (
    <div
      data-testid={`inv-audit-line-${line.id}`}
      className={`rounded-2xl border p-4 transition-colors ${
        counted
          ? hasVariance ? "border-amber-200 bg-amber-50/30" : "border-stone-100 bg-white"
          : "border-stone-100 bg-white"
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center shrink-0 text-[11px] font-medium text-stone-500">
          {monogram}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-stone-900 truncate">{line.product_display_name}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400 mt-0.5">
            {line.product_sku} · expected {line.expected_qty}
          </div>
        </div>
        <button
          onClick={() => setNoteOpen((o) => !o)}
          data-testid={`inv-audit-note-toggle-${line.id}`}
          className={`shrink-0 p-1.5 rounded-full transition-colors ${
            note ? "bg-stone-900 text-white" : "text-stone-400 hover:text-stone-700 hover:bg-stone-50"
          }`}
          title="Add a note"
          aria-label="Add note"
        >
          <StickyNote size={13} />
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => bump(-1)}
          disabled={disabled}
          data-testid={`inv-audit-dec-${line.id}`}
          className="w-9 h-9 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50 disabled:opacity-40"
        >
          <Minus size={13} />
        </button>
        <input
          type="number"
          inputMode="numeric"
          value={val}
          onChange={(e) => { setVal(e.target.value); setDirty(true); }}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
          disabled={disabled}
          placeholder="—"
          data-testid={`inv-audit-count-${line.id}`}
          className="flex-1 h-9 text-center text-lg font-medium text-stone-900 outline-none border-b-2 border-transparent focus:border-stone-900 disabled:opacity-40 min-w-0"
        />
        <button
          onClick={() => bump(1)}
          disabled={disabled}
          data-testid={`inv-audit-inc-${line.id}`}
          className="w-9 h-9 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50 disabled:opacity-40"
        >
          <Plus size={13} />
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider">
        {counted ? (
          <>
            <span
              className={hasVariance ? "text-amber-700" : "text-stone-400"}
              data-testid={`inv-audit-variance-${line.id}`}
            >
              {hasVariance
                ? `variance ${variance > 0 ? "+" : ""}${variance}`
                : "matches expected"}
            </span>
            <button
              onClick={sameAsExpected}
              disabled={disabled}
              data-testid={`inv-audit-same-${line.id}`}
              className="text-stone-400 hover:text-stone-800"
            >
              Reset to expected
            </button>
          </>
        ) : (
          <>
            <span className="text-stone-300">not counted</span>
            <button
              onClick={sameAsExpected}
              disabled={disabled}
              data-testid={`inv-audit-same-${line.id}`}
              className="text-stone-400 hover:text-stone-800"
            >
              Same as expected
            </button>
          </>
        )}
      </div>

      {noteOpen && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => onSave(line.id, { counted_qty: line.counted_qty, notes: note })}
          placeholder="Add a short note…"
          data-testid={`inv-audit-note-${line.id}`}
          className="mt-3 w-full text-xs rounded-lg border border-stone-100 bg-stone-50/40 p-2 outline-none focus:border-stone-300 resize-none"
          rows={2}
        />
      )}
    </div>
  );
}
